import { SiteLocale } from '@graphql/generated';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppContext } from '@lib/context/appContext';
import t, { Labels } from '@lib/locales';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type Props = {
  locale: SiteLocale;
  orderId: string;
  couponCode: string;
  onSuccess: () => void;
};

export const Coupon = ({ locale, orderId, couponCode, onSuccess }: Props) => {
  const { client } = useAppContext();
  const [ui, setUi] = useState({ errorMessage: '', loading: false });

  const schema = z.object({
    code: z
      .string()
      //.min(8).max(40) // done in addCoupon()
      .toUpperCase()
      .trim(),
  });

  type Schema = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Schema>({
    resolver: zodResolver(schema),
  });

  const addCoupon = async (data: Schema) => {
    let errorMessage = '';
    const code = data.code;
    if (!code) {
      setUi({ errorMessage, loading: false });
      return;
    }
    if (code.length < 8 || code.length > 40) {
      errorMessage = t(locale, 'couponOrGift.error.gift_card_or_coupon_code');
      setUi({ errorMessage, loading: false });
      return;
    }
    setUi({ errorMessage: '', loading: true });
    try {
      await client.addCoupon({ orderId, code });
      onSuccess();
    } catch (error: unknown) {
      //errorMessage = t(locale, 'invalid_coupon_code');
      errorMessage = t(locale, 'couponOrGift.error.gift_card_or_coupon_code');
      //message: t("couponOrGift.error.coupon_code"),
      //message: t("couponOrGift.error.gift_card_code"),
    } finally {
      setUi({ errorMessage, loading: false });
    }
  };

  const removeCoupon = async () => {
    let errorMessage = '';
    setUi({ errorMessage, loading: true });
    try {
      await client.removeCoupon(orderId);
      onSuccess();
    } catch (error: unknown) {
      errorMessage = t(locale, 'invalid_coupon_code');
    } finally {
      setUi({ errorMessage, loading: false });
    }
  };

  const onSubmit = (data: Schema) => addCoupon(data);

  return (
    <>
      <form autoComplete="no" onSubmit={handleSubmit(onSubmit)}>
        <label htmlFor="code-input" className="coupon-label">
          {t(locale, 'couponOrGift.label' as Labels)}
        </label>

        <div className="coupon-input">
          <input
            type="text"
            className={ui.errorMessage ? 'input-base -error' : 'input-base'}
            placeholder={t(locale, `couponOrGift.placeholder.gift_card_or_coupon_code` as Labels)}
            {...register('code')}
          />
          <button type="submit" disabled={ui.loading}>
            {t(locale, 'couponOrGift.submit' as Labels)}
          </button>
        </div>
      </form>
      {ui.loading && <div> {t(locale, 'loading')}</div>}
      <span className="coupon-error">
        {ui.errorMessage} {errors.code?.message?.toString()}
      </span>
      {couponCode && (
        <div className="coupon-list">
          <div className="select-all">{couponCode}</div>
          <button type="button" className="coupon-remove" onClick={() => removeCoupon()}>
            {t(locale, 'remove')}
          </button>
        </div>
      )}
    </>
  );
};
